// SPDX-License-Identifier: MIT
// SQL Panel Types for Atlas Viewer

/**
 * Default maximum number of rows to display in the results table UI.
 * Full result set is preserved for "Add to Map" functionality.
 */
export const DEFAULT_DISPLAY_ROW_LIMIT = 1000;

/**
 * Represents a single column in a query result
 */
export interface SqlColumn {
  name: string;
  type: string;
  nullable: boolean;
}

/**
 * Represents a query execution result with dual storage:
 * - `rows`: Full result set (for Add to Map, preserves all data)
 * - `displayRows`: Limited preview slice (for UI table rendering, max DEFAULT_DISPLAY_ROW_LIMIT)
 */
export interface QueryResult {
  columns: SqlColumn[];
  /** Full result set - used by "Add to Map" to create complete Kepler datasets */
  rows: Record<string, unknown>[];
  /** Preview slice for UI rendering - limited to DEFAULT_DISPLAY_ROW_LIMIT for performance */
  displayRows: Record<string, unknown>[];
  rowCount: number;
  executionTimeMs: number;
  /** Source dataset IDs that contributed to this result (for security lineage) */
  sourceDatasetIds: string[];
  /** Whether results were truncated due to query LIMIT */
  isTruncated?: boolean;
  /** The query limit that was applied */
  limit?: number;
}

/**
 * Table schema information for the schema browser
 */
export interface TableSchema {
  tableName: string;
  datasetId: string;
  columns: SqlColumn[];
  rowCount: number;
  /** Whether this table contains secure data (Azure provider) */
  isSecure: boolean;
}

/**
 * State for a single query execution
 */
export interface QueryExecutionState {
  isExecuting: boolean;
  result: QueryResult | null;
  error: string | null;
  lastExecutedAt: number | null;
}

/**
 * Redux state for the SQL panel
 */
export interface SqlPanelState {
  /** Currently active SQL in the editor */
  currentSql: string;
  /** Current execution state */
  execution: QueryExecutionState;
  /** Available table schemas */
  schemas: TableSchema[];
  /** Whether the panel is open */
  isPanelOpen: boolean;
  /** Whether tables are currently being synced to DuckDB */
  isSyncingTables: boolean;
}

/**
 * Result of adding query results to the map
 */
export interface AddToMapResult {
  success: boolean;
  datasetId?: string;
  layerId?: string;
  error?: string;
}

/**
 * Security lineage tracking for export gating
 */
export interface QueryLineage {
  queryId: string;
  sourceDatasetIds: string[];
  hasSecureSource: boolean;
  executedAt: number;
}

/**
 * DuckDB database initialization status
 */
export type DuckDBStatus = 'uninitialized' | 'initializing' | 'ready' | 'error';

/**
 * Options for query execution
 * Note: All rows from query results are extracted and stored in QueryResult.rows.
 * Display is limited to DEFAULT_DISPLAY_ROW_LIMIT in QueryResult.displayRows.
 */
export interface QueryExecutionOptions {
  /** Timeout in milliseconds */
  timeout?: number;
}
